import { useLazyQuery, useSubscription } from '@apollo/client'
import { Typography, IconButton, LinearProgress } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Skeleton } from '@material-ui/lab'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  AUDIT_LOG_INTEGRITY_QUERY,
  AUDIT_LOG_JOB_HANDLER_SUB
} from '../../../../graphql/adminConfig'
import { getFormattedNow } from '../../../../helpers/date'
import { getTranslate, lang } from '../../../../localization'
import {
  AuditLogsIntegrityJobStatus,
  AuditLogsIntegrityResponse,
  LogIntegrityStatus
} from '../../../../types/adminConfig'
import StateBox from '../StateBox'
import useStyles from './index.styles'

type Props = {
  integrityStatus: LogIntegrityStatus[] | undefined
  loadingForAdminConfig: boolean
  setIntegrityStatus: React.Dispatch<React.SetStateAction<LogIntegrityStatus[]>>
}

export default function IntegrityStatus({
  integrityStatus,
  loadingForAdminConfig,
  setIntegrityStatus
}: Props) {
  const classes = useStyles()
  const [trackingID, setTrackingID] = useState<string>('')
  const [progress, setProgress] = useState<number>(0)

  const [getAuditLogsIntegrity] = useLazyQuery<{
    auditLogsIntegrity: AuditLogsIntegrityResponse
  }>(AUDIT_LOG_INTEGRITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setTrackingID(res.auditLogsIntegrity.trackingID)
      setProgress(0)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const { data: auditLogsJobHandlerData } = useSubscription<{
    auditLogsJobHandler: AuditLogsIntegrityJobStatus
  }>(AUDIT_LOG_JOB_HANDLER_SUB, {
    variables: { trackingID },
    skip: !trackingID,
    fetchPolicy: 'network-only',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    const event = auditLogsJobHandlerData?.auditLogsJobHandler
    if (!event) return

    setProgress(event.progressPercent)

    setIntegrityStatus((prev) => {
      const done = event.status === 'Completed' || event.status === 'Failed'
      return prev?.map((row) =>
        row.name === 'Audit Logs'
          ? {
              ...row,
              status: event.status === 'Completed',
              checkedAt: done ? getFormattedNow(lang) : row.checkedAt
            }
          : row
      )
    })
  }, [auditLogsJobHandlerData])

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Integrity Status Breakdown')}
        </Typography>
        <Typography className={classes.subtitle}>
          {getTranslate('Detailed status for each system component')}
        </Typography>
      </div>
      <div className={classes.list}>
        {loadingForAdminConfig
          ? Array(3)
              .fill('')
              .map((_, skeletonIndex) => (
                <div className={classes.row} key={skeletonIndex}>
                  <Typography className={classes.left}>
                    <Skeleton animation="wave" className={classes.skeleton} />
                  </Typography>
                  <div className={classes.right}>
                    <Skeleton animation="wave" className={classes.skeleton} />
                    <Skeleton animation="wave" className={classes.skeleton} />
                  </div>
                </div>
              ))
          : integrityStatus?.map((row, index) => (
              <div key={index} className={classes.row}>
                <Typography className={classes.left}>{row.name}</Typography>
                <div className={classes.right}>
                  {row.name === 'Audit Logs' && (
                    <>
                      <div style={{ width: '400px' }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                        />
                      </div>
                      <IconButton onClick={() => getAuditLogsIntegrity()}>
                        <RefreshIcon />
                      </IconButton>
                    </>
                  )}
                  <StateBox
                    status={row.status}
                    inprogress={
                      auditLogsJobHandlerData?.auditLogsJobHandler?.status ===
                      'InProgress'
                    }
                  />
                  <Typography className={classes.time}>
                    {row.checkedAt}
                  </Typography>
                </div>
              </div>
            ))}
      </div>
    </div>
  )
}
